/**
 * Formatting utilities for display values.
 */

/**
 * Format a number as currency (compact form: $425K, $1.2M).
 * Use formatCurrencyExact() for full precision.
 */
export function formatCurrency(value: number | null | undefined): string {
  if (value == null || value === 0) return '—';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${Math.round(value / 1000)}K`;
  return `$${value.toLocaleString()}`;
}

/**
 * Format a number as currency with full precision (no rounding).
 * Displays exact dollars: $4,567.89 or $1,234,567
 */
export function formatCurrencyExact(value: number | null | undefined, decimals = 0): string {
  if (value == null || value === 0) return '—';
  return `$${value.toLocaleString('en-US', { 
    minimumFractionDigits: decimals, 
    maximumFractionDigits: decimals 
  })}`;
}

/**
 * Format price per acre.
 */
export function formatPricePerAcre(price: number | null | undefined, acres: number | null | undefined): string {
  if (!price || !acres || acres === 0) return '—';
  const ppa = price / acres;
  if (ppa >= 1000) return `$${Math.round(ppa / 1000)}K`;
  return `$${Math.round(ppa).toLocaleString()}`;
}

/**
 * Format location from property/opportunity fields.
 */
export function formatPropertyLocation(property: { 
  city?: string | null; 
  state?: string | null; 
  county?: string | null;
}): string {
  const parts: string[] = [];
  if (property.city) parts.push(property.city);
  if (property.state) parts.push(property.state);
  if (property.county) parts.push(`${property.county} County`);
  return parts.join(', ');
}

/**
 * Format location from opportunity fields.
 * Alias for formatPropertyLocation for clarity.
 */
export function formatOpportunityLocation(opportunity: { 
  city?: string | null; 
  state?: string | null; 
  county?: string | null;
}): string {
  return formatPropertyLocation(opportunity);
}

/**
 * NC County GIS/Tax Record URLs
 * Each county has different systems - this maps known counties to their record lookup URLs.
 * Returns null if county is not supported.
 */
const NC_COUNTY_RECORD_URLS: Record<string, { 
  name: string;
  taxUrl?: string; 
  gisUrl?: string;
  searchParam?: string; // Query param for parcel ID
}> = {
  halifax: {
    name: 'Halifax',
    taxUrl: 'https://taxadmin.halifaxnc.com/TAXOnline/',
    gisUrl: 'https://gis.halifaxnc.com/',
  },
  franklin: {
    name: 'Franklin',
    taxUrl: 'https://tax.franklincountync.us/',
    gisUrl: 'https://gis.franklincountync.us/',
  },
  warren: {
    name: 'Warren',
    taxUrl: 'https://warrencountytax.com/',
    gisUrl: 'https://gis.warrencountync.com/',
  },
  vance: {
    name: 'Vance',
    taxUrl: 'https://taxoffice.vancecounty.org/',
    gisUrl: 'https://gis.vancecounty.org/',
  },
  granville: {
    name: 'Granville',
    taxUrl: 'https://tax.granvillecounty.org/',
    gisUrl: 'https://gis.granvillecounty.org/',
  },
  nash: {
    name: 'Nash',
    taxUrl: 'https://nash.ustaxdata.com/',
    gisUrl: 'https://gis.co.nash.nc.us/',
  },
  northampton: {
    name: 'Northampton',
    taxUrl: 'https://northamptoncountync.gov/departments/tax/',
    gisUrl: 'https://nconemap.gov/', // Falls back to NC OneMap
  },
};

/**
 * Get county record URLs for viewing parcel data.
 * Returns URLs for tax records and GIS maps where available.
 */
export function getCountyRecordUrls(county: string | null | undefined, parcelId?: string | null): {
  taxUrl: string | null;
  gisUrl: string | null;
  ncOneMapUrl: string;
} {
  const ncOneMapUrl = 'https://www.nconemap.gov/pages/parcels';
  
  if (!county) {
    return { taxUrl: null, gisUrl: null, ncOneMapUrl };
  }
  
  const countyKey = county.toLowerCase().replace(/\s+/g, '');
  const countyConfig = NC_COUNTY_RECORD_URLS[countyKey];
  
  if (!countyConfig) {
    return { taxUrl: null, gisUrl: null, ncOneMapUrl };
  }
  
  return {
    taxUrl: countyConfig.taxUrl || null,
    gisUrl: countyConfig.gisUrl || null,
    ncOneMapUrl,
  };
}

